import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  donationService,
  campaignService,
  volunteerService,
} from '../services/api';
import {
  People,
  Campaign,
  AttachMoney,
  TrendingUp,
  Repeat,
} from '@mui/icons-material';

const CHART_COLORS = ['#2c5f2d', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'];
const PIE_COLORS = ['#2c5f2d', '#7c3aed'];

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalDonations: 0,
    totalAmount: 0,
    activeCampaigns: 0,
    pendingVolunteers: 0,
    recurringDonations: 0,
    recurringAmount: 0,
  });
  const [donationList, setDonationList] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [donationsRes, campaignsRes, volunteersRes] = await Promise.all([
          donationService.getAllDonations({ limit: 500 }),
          campaignService.getAllCampaigns({ limit: 100 }),
          volunteerService.getAllApplications({ status: 'pending', limit: 100 }),
        ]);

        const donationData = donationsRes.data?.data || donationsRes.data || [];
        const campaignData = campaignsRes.data?.data || campaignsRes.data || [];
        const volunteerData = volunteersRes.data?.data || volunteersRes.data || [];

        const completedDonations = Array.isArray(donationData)
          ? donationData.filter((d) => d.payment_status === 'completed')
          : [];
        const totalAmount = completedDonations.reduce(
          (sum, d) => sum + (parseInt(d.amount, 10) || 0),
          0
        );
        const recurringDonations = completedDonations.filter(
          (d) => d.is_recurring === true || d.is_recurring === 1
        );
        const recurringAmount = recurringDonations.reduce(
          (sum, d) => sum + (parseInt(d.amount, 10) || 0),
          0
        );

        setStats({
          totalDonations: completedDonations.length,
          totalAmount,
          activeCampaigns: campaignData.filter((c) => c.status === 'active').length,
          pendingVolunteers: volunteerData.length,
          recurringDonations: recurringDonations.length,
          recurringAmount,
        });
        setDonationList(completedDonations);
        setCampaignList(Array.isArray(campaignData) ? campaignData : []);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Donations over time (last 6 months by month)
  const donationsByMonth = useMemo(() => {
    if (!donationList.length) return [];
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        name: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
        amount: 0,
        count: 0,
        month: d.getMonth(),
        year: d.getFullYear(),
      });
    }
    donationList.forEach((d) => {
      const date = d.created_at ? new Date(d.created_at) : null;
      if (!date) return;
      const amt = parseInt(d.amount, 10) || 0;
      const m = months.find((x) => x.month === date.getMonth() && x.year === date.getFullYear());
      if (m) {
        m.amount += amt;
        m.count += 1;
      }
    });
    return months;
  }, [donationList]);

  // Top campaigns by amount raised
  const topCampaignsByAmount = useMemo(() => {
    if (!donationList.length || !campaignList.length) return [];
    const byCampaign = {};
    donationList.forEach((d) => {
      const cid = d.campaign_id != null ? String(d.campaign_id) : 'general';
      if (!byCampaign[cid]) byCampaign[cid] = { campaignId: cid, amount: 0, count: 0 };
      byCampaign[cid].amount += parseInt(d.amount, 10) || 0;
      byCampaign[cid].count += 1;
    });
    const campaignNames = {};
    campaignList.forEach((c) => {
      campaignNames[String(c.id)] = c.title || c.name || `Campaign #${c.id}`;
    });
    if (byCampaign.general) campaignNames.general = 'General';
    return Object.entries(byCampaign)
      .map(([id, v]) => ({
        name: (campaignNames[id] || `Campaign #${id}`).slice(0, 18),
        amount: v.amount,
        count: v.count,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);
  }, [donationList, campaignList]);

  // One-time vs Recurring (by amount)
  const oneTimeVsRecurring = useMemo(() => {
    if (!donationList.length) return [{ name: 'One-time', value: 0 }, { name: 'Recurring', value: 0 }];
    let oneTime = 0;
    let recurring = 0;
    donationList.forEach((d) => {
      const amt = parseInt(d.amount, 10) || 0;
      if (d.is_recurring === true || d.is_recurring === 1) recurring += amt;
      else oneTime += amt;
    });
    return [
      { name: 'One-time', value: oneTime },
      { name: 'Recurring', value: recurring },
    ].filter((s) => s.value > 0);
  }, [donationList]);

  const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        background: '#ffffff',
        border: '1px solid #e8ecf1',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderColor: '#d0d7de',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#667085', fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color, fontSize: '1.5rem' }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: bgColor,
              borderRadius: '10px',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 28, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, height = 280 }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        background: '#ffffff',
        border: '1px solid #e8ecf1',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a202c', mb: 1.5, fontSize: '0.9375rem' }}>
          {title}
        </Typography>
        <Box sx={{ width: '100%', height }}>{children}</Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', py: 3, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a202c', mb: 0.5 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085', fontSize: '0.875rem' }}>
            Overview of your NGO's activity.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={AttachMoney}
              title="Total Donations"
              value={`₹${stats.totalAmount.toLocaleString()}`}
              color="#059669"
              bgColor="#d1fae5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={TrendingUp}
              title="Successful Donations"
              value={stats.totalDonations}
              color="#2c5f2d"
              bgColor="#dbeafe"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={Campaign}
              title="Active Campaigns"
              value={stats.activeCampaigns}
              color="#dc2626"
              bgColor="#fee2e2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={People}
              title="Pending Volunteers"
              value={stats.pendingVolunteers}
              color="#9333ea"
              bgColor="#f3e8ff"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={Repeat}
              title="Recurring Donations"
              value={stats.recurringDonations}
              color="#7c3aed"
              bgColor="#f5f3ff"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={Repeat}
              title="Recurring Amount"
              value={`₹${stats.recurringAmount.toLocaleString()}`}
              color="#6366f1"
              bgColor="#eef2ff"
            />
          </Grid>

          {/* Donations over time (Line chart) */}
          <Grid item xs={12} md={8}>
            <ChartCard title="Donations over time (last 6 months)">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={donationsByMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf1" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#667085" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#667085" tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000) + 'k' : v}`} />
                  <Tooltip
                    formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Amount']}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ borderRadius: '8px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#2c5f2d"
                    strokeWidth={2}
                    dot={{ fill: '#2c5f2d', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Amount"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          {/* One-time vs Recurring (Pie) */}
          <Grid item xs={12} md={4}>
            <ChartCard title="One-time vs Recurring (by amount)">
              {oneTimeVsRecurring.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={oneTimeVsRecurring}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {oneTimeVsRecurring.map((_, index) => (
                        <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Amount']}
                      contentStyle={{ borderRadius: '8px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No donation data yet</Typography>
                </Box>
              )}
            </ChartCard>
          </Grid>

          {/* Top campaigns by amount (Bar chart) */}
          <Grid item xs={12}>
            <ChartCard title="Top campaigns by amount raised" height={320}>
              {topCampaignsByAmount.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topCampaignsByAmount}
                    layout="vertical"
                    margin={{ top: 8, right: 24, left: 80, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf1" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#667085" tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000) + 'k' : v}`} />
                    <YAxis type="category" dataKey="name" width={76} tick={{ fontSize: 11 }} stroke="#667085" />
                    <Tooltip
                      formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Amount']}
                      contentStyle={{ borderRadius: '8px' }}
                    />
                    <Bar dataKey="amount" fill="#2c5f2d" radius={[0, 4, 4, 0]} name="Amount" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No campaign donation data yet</Typography>
                </Box>
              )}
            </ChartCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardPage;
